import { FormikActions } from 'formik';
import React, { ReactNode } from 'react';
import Form from '../../../app/common/components/Form';
import CurrentPasswordField from '../../../app/common/fields/password/CurrentPasswordField';
import NewPasswordField, {
    PasswordValidationState,
} from '../../../app/common/fields/password/NewPassword';
import { IUpdatePasswordRequest } from './updatePasswordRequest';
import { getChallengeData, clearChallengeInput } from '../../../utils/challengerMain';
import { ValidatorRule } from '../../../app/common/validator/validatorRule.constants';
import styled from 'styled-components';
import { FormManditoryAstrixMessage } from '../../../app/common/components/FormManitoryMessage';
import SessionRevocationWarning from '../SessionRevocationWarning';
import { InfoTooltipWrapper } from '../../../app/common/components/InfoTooltip';
import { WarningIconOutlined } from '../../../app/common/components/icons/WarningIconOutlined';
import { addDebouncedRatEvent } from '../../../utils/ratDebounce';

const Section = styled.section`
    margin-top: 50px;
    margin-bottom: 60px;
`;

const Section3 = styled.section`
    margin-top: 50px;
    margin-bottom: 40px;
`;

const Section2 = styled.section`
    margin-top: 25px;
    margin-bottom: 40px;
`;

export const ErrorBox = styled.div`
    background-color: #ffdbdb;
    color: #df0101;
    padding: 8px;
    font-size: 14px;
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

export const CheckboxWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 10px 0px;
    gap: 8px;

    & label {
        font-size: 0.875rem;
        padding-right: 17px;
    }

    ${InfoTooltipWrapper} {
        top: 50%;
        transform: translateY(-40%);
    }
`;

export interface IUpdatePasswordFormValues {
    currentPassword: string;
    password: string;
    repeatPassword: string;
}

export type UpdatePasswordFormProps = IUpdatePasswordFormStateProps &
    IUpdatePasswordFormDispatchProps;

export interface IUpdatePasswordFormStateProps {
    challengerPid: string;
    clientId: string;
    member: any;
    isSubmitting: boolean;
    newPasswordValidatorRules: ValidatorRule[];
    challengerDegenerate?: boolean;
    errorBoxMessage: ReactNode;
}

export interface IUpdatePasswordFormDispatchProps {
    updatePassword: (values: IUpdatePasswordRequest) => any;
    hideBox: () => void;
}

export interface IUpdatePasswordState {
    initialValues: IUpdatePasswordFormValues;
    showPassword: boolean;
    isNewPasswordFocused: boolean;
    passwordValidationState?: PasswordValidationState;
    repeatPasswordValidationState?: PasswordValidationState;
}

export class UpdatePasswordForm extends React.Component<
    UpdatePasswordFormProps,
    IUpdatePasswordState
> {
    private isChallengeLoaded: boolean;

    constructor(props: UpdatePasswordFormProps) {
        super(props);

        const { challengerPid } = this.props;

        const initialValues: IUpdatePasswordFormValues = {
            currentPassword: '',
            password: '',
            repeatPassword: '',
        };

        this.state = {
            showPassword: false,
            isNewPasswordFocused: false,
            initialValues,
        };

        this.isChallengeLoaded = !challengerPid;

        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.onChangeShowPassword = this.onChangeShowPassword.bind(this);
        this.handleNewPasswordFocusChange = this.handleNewPasswordFocusChange.bind(this);
        this.handlePasswordValidationChange = this.handlePasswordValidationChange.bind(this);
        this.handleRepeatPasswordValidationChange =
            this.handleRepeatPasswordValidationChange.bind(this);
        this.onSubmitAttempt = this.onSubmitAttempt.bind(this);
    }

    public render() {
        const { isSubmitting, challengerPid } = this.props;
        const { initialValues } = this.state;
        return (
            <Form
                buttonTextLabel="button.save"
                value={initialValues}
                handleSubmit={this.handleSubmit}
                renderFormFields={this.renderFormFields}
                renderBeforeButton={() => <SessionRevocationWarning />}
                loading={isSubmitting}
                challengerPid={challengerPid}
                saveButtonProps={{
                    qaId: 'submit-update-password',
                    width: '100%',
                }}
                onSubmitAttempt={this.onSubmitAttempt}
            />
        );
    }

    private handleSubmit(
        values: IUpdatePasswordFormValues,
        actions: FormikActions<IUpdatePasswordFormValues>,
    ) {
        this.dismissBoxWarning();
        const { clientId, updatePassword, challengerPid } = this.props;
        updatePassword({
            challengeData: getChallengeData(challengerPid),
            clientId,
            currentPassword: values.currentPassword,
            newPassword: values.password,
        });
        actions.resetForm();
        clearChallengeInput();
    }

    public handleNewPasswordFocusChange = (isFocused: boolean) => {
        this.setState({ isNewPasswordFocused: isFocused });
        this.dismissBoxWarning();
    };

    private onChangeShowPassword(e: any) {
        this.setState({ showPassword: e.target.checked });
    }

    public dismissBoxWarning() {
        const { hideBox } = this.props;
        hideBox();
    }

    private handlePasswordValidationChange(validationState: PasswordValidationState) {
        this.setState({ passwordValidationState: validationState });
    }

    private handleRepeatPasswordValidationChange(validationState: PasswordValidationState) {
        this.setState({ repeatPasswordValidationState: validationState });
    }

    private onSubmitAttempt() {
        const { passwordValidationState, repeatPasswordValidationState } = this.state;

        if (!passwordValidationState && !repeatPasswordValidationState) {
            return;
        }

        const hasWeak = passwordValidationState?.hasWeakPassword || false;
        const hasInvalid = passwordValidationState?.hasInvalidPassword || false;
        const hasMismatch = repeatPasswordValidationState?.hasMismatch || false;

        // Determine which event to fire based on validation errors
        if (hasInvalid && hasMismatch && hasWeak) {
            addDebouncedRatEvent('password_update', 'password_invalid_mismatch_weak');
        } else if (hasInvalid && hasMismatch) {
            addDebouncedRatEvent('password_update', 'password_invalid_mismatch');
        } else if (hasWeak && hasMismatch) {
            addDebouncedRatEvent('password_update', 'password_weak_mismatch');
        } else if (hasInvalid && hasWeak) {
            addDebouncedRatEvent('password_update', 'password_invalid_weak');
        } else if (hasMismatch) {
            addDebouncedRatEvent('password_update', 'password_mismatch');
        } else if (hasInvalid) {
            addDebouncedRatEvent('password_update', 'password_invalid_ui');
        } else if (hasWeak) {
            addDebouncedRatEvent('password_update', 'password_weak_ui');
        }
    }

    private renderFormFields(values: IUpdatePasswordFormValues) {
        const { member, newPasswordValidatorRules, errorBoxMessage } = this.props;
        return (
            <React.Fragment>
                <FormManditoryAstrixMessage />

                <Section>
                    <CurrentPasswordField
                        id="currentPassword"
                        labelId="label.current_password"
                        value={values.currentPassword}
                        required={true}
                    />
                </Section>

                <Section2>
                    <NewPasswordField
                        id="password"
                        labelId="label.new_pass"
                        value={values.password}
                        required={true}
                        currentEmail={member.email}
                        username={member.username}
                        validatorRules={newPasswordValidatorRules}
                        currentPassword={values.currentPassword}
                        showPassword={this.state.showPassword}
                        onNewPasswordFocusChange={this.handleNewPasswordFocusChange}
                        isNewPasswordFocused={this.state.isNewPasswordFocused}
                        onValidationStateChange={this.handlePasswordValidationChange}
                    />
                </Section2>

                <Section3>
                    <NewPasswordField
                        id="repeatPassword"
                        labelId="label.repeat_password"
                        value={values.repeatPassword}
                        required={true}
                        currentEmail={member.email}
                        username={member.username}
                        currentPassword={values.currentPassword}
                        validatorRules={newPasswordValidatorRules}
                        firstPassword={values.password}
                        qaId="repeat-new-password"
                        onChangeShowPassword={this.onChangeShowPassword}
                        showPassword={this.state.showPassword}
                        onValidationStateChange={this.handleRepeatPasswordValidationChange}
                    />
                </Section3>
                {errorBoxMessage && (
                    <ErrorBox>
                        <div>
                            <WarningIconOutlined />
                        </div>
                        {errorBoxMessage}
                    </ErrorBox>
                )}
            </React.Fragment>
        );
    }
}
